import { useLocation, useNavigate } from "react-router";
import { usePlayerInfoQuery } from "../../tanstack/hooks/usePlayerQuery";
import { PropsWithChildren, useEffect, useRef } from "react";
import Loader from "../../components/Loader";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ExecutionsListResponse } from "../../tanstack/queries/player";
import { resetBets } from "../../stores/cardsStore/CardsStore";
import { useStatusStore } from "../../stores/statusStore/useStatusStore";
import { setActiveExecution } from "../../stores/statusStore/statusStore";
import useGetSessionId from "../../hooks/useGetSessionId";
import firePostMessage from "../../utils/firePostMessage";

function RouteWrapper({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const sessionId = useGetSessionId();
  const { activeExecutionId, activeYantraId } = useStatusStore();

  const queryClient = useQueryClient();
  const executionsDataRef = useRef(queryClient.getQueryData<ExecutionsListResponse>(["executions"]));

  useEffect(() => {
    if (activeExecutionId === "" || activeYantraId === "") return () => {};
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] === "executions") {
        executionsDataRef.current = event.query.state.data;
        if (activeExecutionId !== "") {
          // check if we should update activeExecutionId
          const executionsData = executionsDataRef.current;
          const nextExecution = executionsData?.yantraExecutions.find(
            (exec) => exec.yantraId === activeYantraId && exec.id !== activeExecutionId && exec.status === "RUNNING"
          );
          if (nextExecution && nextExecution.id !== activeExecutionId) {
            setActiveExecution(nextExecution.id, nextExecution.yantraId);
            resetBets();

            navigate({ pathname: `/${nextExecution.id}`, search }, { replace: true });

            firePostMessage({ type: "round_change", id: nextExecution.id });
          }
        }
      }
    });

    return () => unsubscribe();
  }, [queryClient, activeYantraId, activeExecutionId, navigate, search]);

  const { isError, isPending, isFetched, data: userInfo } = usePlayerInfoQuery(sessionId);

  useEffect(() => {
    if (isError) {
      navigate("/error");

      return;
    }

    if (isFetched) {
      if (!userInfo?.canBet || !userInfo?.user.allowedGames.includes("dfcc8b45-d8a5-4418-814a-1c6ebdcc5bed")) {
        navigate("/error");
      }
    }
  }, [navigate, isError, isFetched, userInfo?.canBet, userInfo?.user.allowedGames]);

  if (isPending) {
    return <Loader />;
  }

  return <Box sx={{ position: "relative" }}>{children}</Box>;
}

export default RouteWrapper;
